/**
 * Einzelheiten zu einem angeklickten Objekt nachladen.
 *
 * In den Vektorkacheln steht bewusst wenig: Name, Klasse, Rang. Telefon,
 * Öffnungszeiten und Website wären dort hundertfacher Ballast, den fast
 * niemand sieht. Sie kommen deshalb erst auf Klick - direkt aus OpenStreetMap.
 *
 * Möglich ist das, weil Planetiler die OSM-Kennung in die Kachel schreibt:
 * `id = osm_id * 10 + Typ` mit 1 = Knoten, 2 = Weg, 3 = Relation. Gegen die
 * OSM-Datenbank geprüft, die Namen stimmen überein.
 */
const OSM_API = 'https://api.openstreetmap.org/api/0.6';
const WIKIDATA = 'https://www.wikidata.org/w/rest.php/wikibase/v1/entities/items';

const TYPES = { 1: 'node', 2: 'way', 3: 'relation' };
const cache = new Map();

/** Kachel-Kennung -> {type, id} oder null */
export function osmRef(featureId) {
  if (featureId === undefined || featureId === null) return null;
  const n = Number(featureId);
  if (!Number.isFinite(n) || n <= 0) return null;
  const type = TYPES[n % 10];
  if (!type) return null;
  return { type, id: Math.floor(n / 10) };
}

export function osmUrl(ref) {
  return ref ? `https://www.openstreetmap.org/${ref.type}/${ref.id}` : null;
}

async function cached(key, fn) {
  if (cache.has(key)) return cache.get(key);
  const p = fn().catch(() => null);
  cache.set(key, p);
  return p;
}

/** Alle Merkmale eines OSM-Objekts. */
export function fetchTags(ref) {
  if (!ref) return Promise.resolve(null);
  return cached(`osm:${ref.type}/${ref.id}`, async () => {
    const r = await fetch(`${OSM_API}/${ref.type}/${ref.id}.json`, {
      headers: { Accept: 'application/json' }
    });
    if (!r.ok) throw new Error(String(r.status));
    const d = await r.json();
    return d?.elements?.[0]?.tags || {};
  });
}

/**
 * Deutschsprachigen Artikel finden. Das OSM-Merkmal nennt oft die englische
 * Fassung ("en:Kapellbrücke"); Wikidata kennt zu jedem Objekt die Entsprechung
 * in jeder Sprache - 75 Byte Antwort, das lohnt den zusätzlichen Abruf.
 */
export function fetchGermanTitle(qid) {
  if (!qid || !/^Q\d+$/.test(qid)) return Promise.resolve(null);
  return cached(`wd-de:${qid}`, async () => {
    const r = await fetch(`${WIKIDATA}/${qid}/sitelinks/dewiki`);
    if (!r.ok) throw new Error(String(r.status));
    const d = await r.json();
    return d?.title ? `de:${d.title}` : null;
  });
}

/**
 * Kurzfassung eines Wikipedia-Artikels. Der OSM-Wert hat die Form
 * "de:Luzern" - Sprache und Titel.
 */
export function fetchWikipedia(tag) {
  if (!tag) return Promise.resolve(null);
  const [lang, ...rest] = String(tag).split(':');
  const title = rest.join(':');
  if (!lang || !title) return Promise.resolve(null);
  return cached(`wp:${tag}`, async () => {
    const r = await fetch(
      `https://${lang}.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`
    );
    if (!r.ok) throw new Error(String(r.status));
    const d = await r.json();
    return {
      titel: d.title,
      text: d.extract,
      url: d.content_urls?.desktop?.page || `https://${lang}.wikipedia.org/wiki/${encodeURIComponent(title)}`
    };
  });
}

/**
 * Fläche in km² aus Wikidata (P2046). Steht in OSM nicht, weil sie sich aus
 * der Grenzgeometrie ergibt - und die liegt nicht in den Kacheln.
 */
export function fetchArea(qid) {
  if (!qid || !/^Q\d+$/.test(qid)) return Promise.resolve(null);
  return cached(`wd:${qid}`, async () => {
    const r = await fetch(`${WIKIDATA}/${qid}/statements?property=P2046`);
    if (!r.ok) throw new Error(String(r.status));
    const d = await r.json();
    const s = d?.P2046?.[0]?.value?.content;
    if (!s?.amount) return null;
    const km2 = Number(s.amount);
    return Number.isFinite(km2) ? km2 : null;
  });
}
